import DossierSteps from "../DossierSteps";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useDispatch } from "react-redux";
import { showSnackbar } from "Redux/SnackBar/actions";
import VehicleLeasePurchaseApi from "Services/VehicleLeasePurchase";
import MplusApi from "Services/Mplus";
import DriverApi from "Services/Driver";
import DriverUpdateModal from "Pages/Modals/Driver/Update";
import ProgrammeMPlusFinancing from "../Financing";
import ProgrammeMPlusContracts from "../Contracts";
import ProgrammeMPlusDelivery from "../Delivery";
import PaymentTrackingContainer from "../PaymentTracking/Container";
import {
  DOCUMENT_STATUS,
  FINANCING_STATUS,
  apiErrorMessage,
  financingReached,
  formatAmount,
  isSigned,
  productFeatures,
  toAssetUrl,
  unwrap,
  unwrapList,
  useHasRole,
} from "../mplusUtils";
import { apiUrl } from "../../../Constants";
import useStyles from "./style";

const getDriver = (item = {}) => item?.driver || item?.driver_rides?.users || item?.driver_rides || {};
const getId = (item = {}) => item?.id || item?._id || item?.lease_purchase_id || "";
const display = (value) => value === null || value === undefined || value === "" ? "--" : value.toString();
const titleCase = (value) => display(value).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());

// Type de demandeur (section 4 de l'API) → libellés de la fiche.
const APPLICANT_TYPES = {
  DRIVER: { profileTitle: "Profil du chauffeur", role: "Chauffeur VTC" },
  USER: { profileTitle: "Profil du demandeur", role: "Utilisateur Metuaa" },
  BUSINESS: { profileTitle: "Profil de l'entreprise", role: "Entreprise" },
};

// Statut de la demande → libellé et style du badge.
const APPLICATION_STATUS = {
  PENDING: { label: "En examen", tone: "pending" },
  APPROVED: { label: "Approuvée", tone: "approved" },
  REJECTED: { label: "Refusée", tone: "rejected" },
};

const mapApplication = (item = {}, index = 0) => {
  const driver = getDriver(item);
  const guarantor = item?.guarantor || item?.guarantor_info || {};
  const financing = item?.financing || item?.financing_details || item?.financingDetails || {};
  // Modèle choisi dans le catalogue M+ (product_catalog), sinon l'ancien champ vehicle.
  const vehicle = item?.product_catalog || item?.vehicle || item?.requested_vehicle || item?.requestedVehicle || {};
  const repaymentMonths = item?.repayment_months ?? (Number.isFinite(Number(item?.repayment_preference)) ? Number(item.repayment_preference) : null);
  return {
    id: getId(item) || `application-${index + 1}`,
    reference: item?.reference || item?.applicant?.reference || getId(item) || `application-${index + 1}`,
    applicantType: (item?.applicant_type || "DRIVER").toString().toUpperCase(),
    applicantCode: item?.applicant?.code || driver?.code || "",
    quantity: item?.quantity ?? 1,
    reviewedAt: item?.reviewed_at || null,
    driverId: item?.applicant_user_id || item?.user_id || item?.userId || driver?.user_id || driver?.userId || driver?._id || driver?.id || "",
    driverName: [driver?.first_name, driver?.last_name].filter(Boolean).join(" ") || driver?.name || item?.applicant?.name || "Demandeur",
    driverEmail: driver?.email || "",
    driverPhone: driver?.phone || driver?.mobile_number || "",
    driverCity: driver?.city || "",
    driverImage: item?.applicant?.photo_url || driver?.image_local || driver?.imageLocal || driver?.profile_image || driver?.profileImage || "",
    declaredCharges: item?.declared_monthly_charges ?? null,
    normalizedDocuments: Array.isArray(item?.normalized_documents) ? item.normalized_documents : null,
    financingRefs: Array.isArray(item?.financings) ? item.financings : [],
    driverStatus: driver?.account_status || driver?.accountStatus || driver?.status || (driver?.is_active ? "active" : ""),
    driverRating: driver?.rating ?? driver?.average_rating ?? driver?.averageRating ?? driver?.rating_average ?? null,
    monthlyIncome: item?.declared_monthly_income ?? item?.monthly_income ?? item?.monthlyIncome ?? financing?.monthly_income ?? driver?.monthly_income ?? null,
    monthlyPayment: item?.monthly_payment ?? item?.monthlyPayment ?? item?.monthly_installment ?? item?.monthlyInstallment ?? financing?.monthly_payment ?? financing?.monthlyInstallment ?? null,
    vehiclePrice: item?.vehicle_price ?? item?.vehiclePrice ?? item?.cash_price ?? item?.cashPrice ?? item?.vehicle_cost ?? financing?.vehicle_price ?? financing?.cash_price ?? vehicle?.unit_cost ?? null,
    downPayment: item?.down_payment ?? item?.downPayment ?? item?.initial_payment ?? financing?.down_payment ?? financing?.initialPayment ?? null,
    annualRate: item?.annual_interest_rate ?? item?.annualInterestRate ?? item?.interest_rate ?? financing?.annual_interest_rate ?? financing?.interest_rate ?? vehicle?.annual_interest_rate ?? null,
    financingDuration: item?.financing_duration_months ?? item?.financingDurationMonths ?? item?.loan_term_months ?? item?.loanTermMonths ?? financing?.duration_months ?? financing?.term_months ?? repaymentMonths,
    unpaidBalance: item?.unpaid_balance ?? item?.unpaidBalance ?? item?.outstanding_balance ?? item?.outstandingBalance ?? driver?.unpaid_balance ?? driver?.outstanding_balance ?? null,
    status: (item?.status || "PENDING").toString().toUpperCase(),
    vehicleType: item?.vehicle_type || vehicle?.type || vehicle?.vehicle_type || vehicle?.category || "",
    vehicleMake: vehicle?.name || item?.preferred_vehicle_make || vehicle?.vehicle_name || vehicle?.model || vehicle?.make || "",
    vehicleColor: item?.preferred_vehicle_color || vehicle?.color || "",
    monthsActive: item?.months_active ?? "--",
    acceptanceRate: item?.trip_acceptance_rate ?? "--",
    cancellationRate: item?.cancellation_rate ?? "--",
    tripsPerWeek: item?.average_number_of_trips_per_week ?? "--",
    repaymentPreference: item?.repayment_preference || "--",
    guarantorName: item?.guarantor_name || guarantor?.name || [guarantor?.first_name, guarantor?.last_name].filter(Boolean).join(" ") || "--",
    guarantorRelationship: item?.guarantor_relationship || guarantor?.relationship || "--",
    guarantorPhone: item?.guarantor_phone_number || guarantor?.phone || guarantor?.phone_number || "--",
    guarantorAddress: item?.guarantor_address || guarantor?.address || "--",
    guarantorEmail: item?.guarantor_email || guarantor?.email || "--",
    guarantorIdNumber: item?.guarantor_id_number || guarantor?.id_number || "--",
    vehicleImage: item?.vehicle_image || item?.vehicleImage || item?.preferred_vehicle_image || vehicle?.image || vehicle?.image_url || "",
    vehicleFuel: item?.vehicle_fuel || item?.fuel_type || item?.fuelType || vehicle?.fuel || vehicle?.fuel_type || "--",
    vehicleTransmission: item?.vehicle_transmission || item?.transmission || vehicle?.transmission || "--",
    createdAt: item?.created_at || null,
    documents: [
      ...(Array.isArray(item?.documents) ? item.documents : []),
      ...(Array.isArray(item?.driver_documents) ? item.driver_documents : []),
      ...(Array.isArray(item?.driverDocuments) ? item.driverDocuments : []),
      ...(Array.isArray(item?.documents_data) ? item.documents_data : []),
      ...(Array.isArray(item?.files) ? item.files : []),
    ],
    raw: item,
  };
};

const documentTypeLabels = ["CNI", "Permis de conduire", "Justificatif de domicile"];
const getDocumentName = (document = {}, index = 0) => {
  const rawType = document?.documentType?.name || document?.documentType?.label || document?.document_type?.name || document?.document_type?.label || document?.document_type_id?.name || document?.document_type_id?.label || document?.document_type_id || document?.document_type || document?.documentType || document?.document_type_name || document?.documentTypeName || document?.document_type_alias || document?.documentTypeAlias || document?.type;
  const type = typeof rawType === "string" ? rawType : "";
  if (type) {
    const normalized = type.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[_-]+/g, " ").trim();
    if (/\b(cni|national identity|identity card|carte nationale|carte d identite)\b/.test(normalized)) return "CNI";
    if (/driver.?s? license|driving license|permis/.test(normalized)) return "Permis de conduire";
    if (/proof of (address|residence)|justificatif|residence certificate/.test(normalized)) return "Justificatif de domicile";
    return titleCase(type);
  }
  return documentTypeLabels[index] || "Document chauffeur";
};

const getDocumentDate = (document = {}) => {
  const value = document?.created_at || document?.createdAt || document?.uploaded_at || document?.uploadedAt || document?.received_at || document?.receivedAt;
  if (!value) return "Reçu";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Reçu";
  return `Reçu le ${date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}`;
};

const ProgrammeMPlusReview = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId } = useParams();
  // La ligne du tableau sert de cache ; attendre la fiche complète avant affichage.
  const [application, setApplication] = useState(location.state?.row ? mapApplication(location.state.row.raw || location.state.row) : null);
  const [loading, setLoading] = useState(true);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [validatingDocumentId, setValidatingDocumentId] = useState(null);
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [confirmAction, setConfirmAction] = useState(null);
  const [saving, setSaving] = useState(false);
  // Dossiers M+ ouverts pour cette demande (un par véhicule, section 6).
  const [financings, setFinancings] = useState([]);
  const [financingId, setFinancingId] = useState("");
  const [eligibility, setEligibility] = useState(null);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [creating, setCreating] = useState(false);
  const [stepLoading, setStepLoading] = useState(true);
  const [eligibilityLoading, setEligibilityLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(true);
  const hasRole = useHasRole();
  const leaseId = decodeURIComponent(applicationId || "");

  const notify = useCallback((message, variant) => {
    dispatch(showSnackbar({
      message,
      variant,
      autoHideDuration: 3000,
      anchorOrigin: { vertical: "top", horizontal: "right" },
    }));
  }, [dispatch]);

  // La ligne du tableau sert d'affichage immédiat ; le détail complet vient de GET /vehicle-lease-purchase/:id.
  useEffect(() => {
    if (!leaseId) return undefined;
    let active = true;
    setLoading(true);
    VehicleLeasePurchaseApi.getVehicleLeasePurchase(leaseId)
      .then((response) => {
        const item = unwrap(response);
        if (active && item) {
          setApplication(mapApplication(item));
          setOverrideReason((previous) => previous || item.eligibility_override_reason || item.derogation_reason || "");
        }
      })
      .catch((error) => {
        if (active) notify(apiErrorMessage(error, "Impossible de charger cette demande M+."), "error");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [leaseId, notify]);

  const productCatalogId = application?.raw?.product_catalog_id || application?.raw?.product_catalog?.id || "";
  const userId = application?.driverId || "";

  // Retrouve les dossiers de la demande : contrats de la demande (un élément par véhicule), sinon liste filtrée.
  const loadFinancings = useCallback(async () => {
    if (!leaseId) return [];
    // 1) filtre vehicle_lease_purchase_id ; 2) contrats de la demande (un élément par véhicule).
    let ids = [];
    try {
      ids = unwrapList(await MplusApi.getFinancings({ vehicle_lease_purchase_id: leaseId, page: 1, limit: 20 }))
        .filter((item) => !item.vehicle_lease_purchase_id || item.vehicle_lease_purchase_id === leaseId)
        .map((item) => item.id);
    } catch (error) {
      ids = [];
    }
    if (!ids.length) {
      try {
        ids = unwrapList(await MplusApi.getApplicationContracts(leaseId)).map((item) => item.financing_id).filter(Boolean);
      } catch (error) {
        ids = [];
      }
    }
    const details = await Promise.all(ids.map((id) => MplusApi.getFinancing(id).then(unwrap).catch(() => null)));
    const list = details.filter(Boolean);
    setFinancings(list);
    setFinancingId((previous) => (list.some((item) => item.id === previous) ? previous : (list.find((item) => item.status !== "CANCELLED") || list[0])?.id || ""));
    return list;
  }, [leaseId]);

  // Étape à ouvrir selon l'avancement du dossier.
  const stepFor = useCallback(async (financing) => {
    if (!financing) return 1;
    if (financingReached(financing.status, "ACTIVE")) return 5;
    if (!financingReached(financing.status, "LOAN_APPROVED")) return 2;
    try {
      const contracts = unwrap(await MplusApi.getFinancingContracts(financing.id)) || {};
      if (isSigned(contracts.sale_contract) && (!contracts.guarantee_contract || isSigned(contracts.guarantee_contract))) return 4;
    } catch (error) {
      return 3;
    }
    return 3;
  }, []);

  useEffect(() => {
    let active = true;
    setStepLoading(true);
    const initializeStep = async () => {
      try {
        const list = await loadFinancings();
        if (!active) return;
        const current = list.find((item) => item.status !== "CANCELLED") || list[0];
        const step = await stepFor(current);
        if (active) setActiveStep(step);
      } finally {
        if (active) setStepLoading(false);
      }
    };
    initializeStep();
    return () => { active = false; };
  }, [loadFinancings, stepFor]);

  // Attendre la fiche complète pour charger l'éligibilité du demandeur.
  useEffect(() => {
    if (loading) return undefined;
    if (!userId || !productCatalogId) {
      setEligibilityLoading(false);
      return undefined;
    }
    let active = true;
    setEligibilityLoading(true);
    MplusApi.getEligibility(userId, {
      product_catalog_id: productCatalogId,
      number_of_repayments: application?.financingDuration || undefined,
      quantity: application?.quantity || 1,
    })
      .then((response) => { if (active) setEligibility(unwrap(response) || null); })
      .catch(() => { if (active) setEligibility(null); })
      .finally(() => { if (active) setEligibilityLoading(false); });
    return () => { active = false; };
  }, [loading, application?.financingDuration, application?.quantity, productCatalogId, userId]);

  // Véhicules disponibles du modèle demandé, pour une réservation à l'ouverture du dossier.
  useEffect(() => {
    if (loading) return undefined;
    if (!productCatalogId) {
      setStockLoading(false);
      return undefined;
    }
    let active = true;
    setStockLoading(true);
    MplusApi.getStock({ status: "AVAILABLE", product_catalog_id: productCatalogId, page: 1, limit: 50 })
      .then((response) => { if (active) setAvailableAssets(unwrapList(response)); })
      .catch(() => { if (active) setAvailableAssets([]); })
      .finally(() => { if (active) setStockLoading(false); });
    return () => { active = false; };
  }, [loading, productCatalogId]);

  const financing = financings.find((item) => item.id === financingId) || null;
  const openFinancings = financings.filter((item) => item.status !== "CANCELLED");
  const canOpenAnother = hasRole("FINANCE") && application?.status === "APPROVED" && openFinancings.length > 0 && openFinancings.length < Number(application?.quantity || 1);
  const notEligible = eligibility ? eligibility.eligible === false : false;

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      const response = await VehicleLeasePurchaseApi.updateVehicleLeasePurchase(leaseId, { status });
      const item = response?.data?.data;
      setApplication((previous) => (item ? mapApplication(item) : { ...previous, status }));
      notify(status === "APPROVED" ? "Demande approuvée." : "Demande rejetée.", "success");
      return true;
    } catch (error) {
      notify(apiErrorMessage(error, "La mise à jour de la demande a échoué."), "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Le dossier est créé à l'étape 2 (formulaire de conditions + simulation identique).
  const startCreation = () => {
    setCreating(true);
    setActiveStep(2);
  };

  const handleApprove = async () => {
    if (financing) {
      setActiveStep(await stepFor(financing));
      return;
    }
    if (application?.status !== "APPROVED" && !(await updateStatus("APPROVED"))) return;
    startCreation();
  };

  const handleCreated = async (created) => {
    setCreating(false);
    setSelectedAsset("");
    await loadFinancings();
    if (created?.id) setFinancingId(created.id);
  };

  const openDossier = async (id) => {
    const target = financings.find((item) => item.id === id);
    setFinancingId(id);
    setCreating(false);
    setActiveStep(await stepFor(target));
  };

  const handleConfirm = async () => {
    if (confirmAction === "reject") {
      if (await updateStatus("REJECTED")) setConfirmAction(null);
      return;
    }
    setSaving(true);
    try {
      await VehicleLeasePurchaseApi.deleteVehicleLeasePurchase(leaseId);
      notify("Demande supprimée.", "success");
      navigate("/programme-m-plus");
    } catch (error) {
      notify(apiErrorMessage(error, "La suppression de la demande a échoué."), "error");
    } finally {
      setSaving(false);
    }
  };

  const documentRows = useMemo(() => application?.documents || [], [application]);
  const driverId = application?.driverId || application?.raw?.driver_id || application?.raw?.driverId || "";
  const photoUrl = application?.driverImage
    ? (/^https?:\/\//i.test(application.driverImage) ? application.driverImage : `${apiUrl}${application.driverImage}`)
    : "";
  const vehicleText = titleCase(application?.vehicleMake) !== "--" ? titleCase(application?.vehicleMake) : titleCase(application?.vehicleType) !== "--" ? titleCase(application?.vehicleType) : "Véhicule à préciser";
  const catalogProduct = application?.raw?.product_catalog || null;
  const vehicleMeta = (catalogProduct
    ? [catalogProduct.category, ...productFeatures(catalogProduct)]
    : [application?.vehicleType, application?.vehicleFuel, application?.vehicleTransmission].map(titleCase))
    .filter((value) => value && value !== "--" && value.toLowerCase() !== vehicleText.toLowerCase())
    .join(" · ");
  const vehicleImagePath = catalogProduct?.image_url || application?.vehicleImage;
  const vehicleImageUrl = vehicleImagePath ? toAssetUrl(vehicleImagePath) : "";
  const vehicleImageStyle = { backgroundImage: `url('${vehicleImageUrl}')`, backgroundSize: "contain", backgroundPosition: "center" };

  const validateDocument = async (document) => {
    if (!driverId || !document.id || validatingDocumentId) return;
    setValidatingDocumentId(document.id);
    try {
      unwrap(await DriverApi.updateDriverDocumentStatus(driverId, document.id, { status: "APPROVED" }));
      const markValidated = (item) => (item.id || item._id) === document.id
        ? { ...item, status: "VALIDATED", verification_status: "VALIDATED" }
        : item;
      setApplication((previous) => ({
        ...previous,
        normalizedDocuments: previous.normalizedDocuments?.map(markValidated) ?? null,
        documents: previous.documents.map(markValidated),
      }));
      setPreviewDocument((previous) => previous?.id === document.id ? { ...previous, status: "VALIDATED" } : previous);
      notify("Document validé.", "success");
    } catch (error) {
      notify(apiErrorMessage(error, "Impossible de valider ce document."), "error");
    } finally {
      setValidatingDocumentId(null);
    }
  };

  const stepProps = { application, financing: creating ? null : financing, notify, onReload: loadFinancings };
  const financingSwitcher = financings.length > 1 && (
    <Box className={classes.switcher}>
      <Typography className={classes.switcherLabel}>Dossier du véhicule</Typography>
      <TextField select size="small" value={financingId} onChange={(event) => setFinancingId(event.target.value)} className={classes.switcherField}>
        {financings.map((item, index) => (
          <MenuItem key={item.id} value={item.id}>
            Véhicule {index + 1}{item.reference ? ` · ${item.reference}` : ""} · {FINANCING_STATUS[item.status]?.label || item.status}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  if (loading || stepLoading || eligibilityLoading || stockLoading) {
    return (
      <Box className={classes.loading} role="status" aria-label="Chargement du dossier" aria-busy="true">
        <CircularProgress size={28} aria-label="Chargement du dossier" />
      </Box>
    );
  }

  if (activeStep > 1 && (financing || creating)) {
    return (
      <>
        {!creating && financingSwitcher}
        {activeStep === 2 && (
          <ProgrammeMPlusFinancing
            {...stepProps}
            eligibility={eligibility}
            photoUrl={photoUrl}
            creation={creating ? {
              leaseId,
              productCatalogId,
              assetId: selectedAsset,
              overrideReason: overrideReason.trim(),
              notEligible,
              defaultMonths: application?.financingDuration,
            } : null}
            onCreated={handleCreated}
            onBack={() => { setCreating(false); setActiveStep(1); }}
            onNext={() => setActiveStep(3)}
          />
        )}
        {activeStep === 3 && <ProgrammeMPlusContracts {...stepProps} onBack={() => setActiveStep(2)} onNext={() => setActiveStep(4)} />}
        {activeStep === 4 && <ProgrammeMPlusDelivery {...stepProps} onBack={() => setActiveStep(3)} onNext={() => setActiveStep(5)} />}
        {activeStep === 5 && <PaymentTrackingContainer {...stepProps} onBack={() => setActiveStep(4)} />}
      </>
    );
  }

  if (!application) {
    return (
      <Box className={classes.page}>
        <Button className={classes.backButton} startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/programme-m-plus")}>Retour aux demandes</Button>
        <Typography className={classes.pageTitle}>Dossier introuvable</Typography>
      </Box>
    );
  }

  const activeStatus = application.driverStatus.toString().toLowerCase();
  const accountActive = ["active", "approved", "enabled"].includes(activeStatus);
  const accountKnown = Boolean(activeStatus);
  const ratingKnown = application.driverRating !== null && application.driverRating !== undefined;
  const unpaidKnown = application.unpaidBalance !== null && application.unpaidBalance !== undefined;
  const paymentKnown = application.monthlyPayment !== null && application.monthlyPayment !== undefined;
  const incomeKnown = application.monthlyIncome !== null && application.monthlyIncome !== undefined;
  const parsedPayment = Number(application.monthlyPayment);
  const parsedIncome = Number(application.monthlyIncome);
  const ratio = paymentKnown && incomeKnown && parsedIncome > 0 ? Math.round((parsedPayment / parsedIncome) * 100) : null;
  const unpaidAmount = Number(application.unpaidBalance);
  const details = [
    { label: "Compte chauffeur actif (LewooPay)", value: accountKnown ? (accountActive ? "Actif" : "Inactif") : "--", good: accountKnown ? accountActive : true },
    { label: "Ancienneté minimale (≥ 6 mois)", value: `${display(application.monthsActive)} mois`, good: Number.isFinite(Number(application.monthsActive)) ? Number(application.monthsActive) >= 6 : true },
    { label: "Note minimale (≥ 4,0)", value: ratingKnown ? display(application.driverRating) : "--", good: ratingKnown ? Number(application.driverRating) >= 4 : true },
    { label: "Aucun impayé", value: unpaidKnown ? formatAmount(application.unpaidBalance) : "--", good: unpaidKnown ? unpaidAmount === 0 : true },
    { label: "Ratio mensualités / revenus (≤ 40 %)", value: ratio === null ? "--" : `${ratio} %`, good: ratio === null ? true : ratio <= 40 },
  ];
  // Contrôles de l'API d'éligibilité quand elle répond, sinon estimation locale.
  const eligibilityRows = eligibility?.checks?.length
    ? eligibility.checks.map((check) => {
      const off = check.level === "OFF";
      const shownValue = check.value == null ? "" : check.code === "DEBT_RATIO" ? `${Number(check.value).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %` : String(check.value);
      return {
        label: check.label || check.code,
        good: off || check.ok,
        off,
        text: off ? "Non applicable" : check.ok ? "Conforme" : check.code === "DEBT_RATIO" && shownValue ? `Limite à ${shownValue}` : check.level === "BLOCKING" ? "Non conforme" : "À vérifier",
        detail: check.code === "DEBT_RATIO"
          ? `Charges : ${formatAmount(eligibility.monthly_charges)} (dont déclarées ${formatAmount(eligibility.declared_monthly_charges)})`
          : "",
      };
    })
    : details.map((item) => ({ label: item.label, good: item.good, text: item.good ? "Conforme" : item.value !== "--" && item.label.startsWith("Ratio") ? `Limite à ${item.value}` : "À vérifier" }));
  const needsReview = eligibility ? notEligible : eligibilityRows.some((row) => !row.good);
  const existingClient = application.raw?.applicant?.existing_client ?? application.raw?.existing_client;
  const reservedAsset = financing?.asset;
  const statusInfo = APPLICATION_STATUS[application.status] || APPLICATION_STATUS.PENDING;
  const applicantInfo = APPLICANT_TYPES[application.applicantType] || APPLICANT_TYPES.DRIVER;
  const identityDocuments = (application.normalizedDocuments ?? documentRows).map((document, index) => {
    const status = String(document.status || document.verification_status || "RECEIVED").toUpperCase();
    const path = document.url || document.document_path || document.documentPath || document.file || document.path || "";
    return {
      id: document.id || document._id,
      label: document.label || getDocumentName(document, index),
      url: toAssetUrl(path),
      mimeType: document.mime_type || document.mimeType || "",
      status: ["APPROVED", "VALIDATED"].includes(status) ? "VALIDATED" : status,
      date: getDocumentDate(document),
    };
  });

  return (
    <Box className={classes.page}>
      <Box className={classes.breadcrumbs}>
        <Button className={classes.backButton} startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/programme-m-plus")}>Gestion des dossiers</Button>
        <span>/</span><span>{application.reference}</span>
      </Box>

      <Box className={classes.headingProgress}>
        <Box className={classes.topLine}>
          <Typography className={classes.pageTitle}>Demande {application.reference}</Typography>
          <Chip className={`${classes.statusChip} ${classes[`status-${statusInfo.tone}`]}`} label={statusInfo.label} />
        </Box>
        <DossierSteps activeStep={1} completedThrough={financing ? 1 : 0} />
      </Box>

      <Box className={classes.profileDocuments}>
        <Box className={classes.card}>
          <Typography className={classes.cardTitle}>{applicantInfo.profileTitle}</Typography>
          <Box className={classes.profileContent}>
            <Avatar className={classes.profileAvatar} src={photoUrl}><PersonOutlineRoundedIcon /></Avatar>
            <Box className={classes.profileInfo}>
              <Typography className={classes.driverName}>{application.driverName}</Typography>
              <Typography className={classes.mutedLine}>{[applicantInfo.role, application.driverCity, application.applicantCode].filter(Boolean).join(" · ")}</Typography>
              <Typography className={classes.contactLine}><LocalPhoneOutlinedIcon />{application.driverPhone || "--"}</Typography>
              <Typography className={classes.contactLine}><MailOutlineRoundedIcon />{application.driverEmail || "--"}</Typography>
            </Box>
          </Box>
          <Box className={classes.profileFooter}>
            {existingClient !== undefined && (
              <Chip className={existingClient ? classes.clientChip : classes.newClientChip} label={existingClient ? "Client existant" : "Nouveau client"} size="small" />
            )}
            {application.applicantType === "DRIVER" && driverId && (
              <Button className={classes.linkButton} onClick={() => setDriverModalOpen(true)}>Voir la fiche chauffeur</Button>
            )}
          </Box>
        </Box>

        <Box className={classes.card}>
          <Typography className={classes.cardTitle}>Documents d’identité</Typography>
          <Box className={classes.documentList}>
            {identityDocuments.length ? identityDocuments.map((document, index) => {
              const docStatus = DOCUMENT_STATUS[document.status] || DOCUMENT_STATUS.RECEIVED;
              return (
                <Box className={classes.documentRow} key={document.id || index}>
                  <DescriptionOutlinedIcon className={classes.documentIcon} />
                  <Box className={classes.documentInfo}>
                    <Typography className={classes.documentName}>{document.label}</Typography>
                    <Typography className={classes.documentMeta}>{document.date}</Typography>
                  </Box>
                  {document.url && (
                    <Tooltip title="Visualiser le document">
                      <IconButton size="small" aria-label={`Visualiser ${document.label}`} onClick={() => setPreviewDocument(document)}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <span className={`${classes.docBadge} ${classes[`docBadge-${docStatus.tone}`]}`}>{docStatus.label}</span>
                  {document.url && document.id && driverId && document.status !== "VALIDATED" && (
                    <Button size="small" disabled={Boolean(validatingDocumentId)} onClick={() => validateDocument(document)}>
                      {validatingDocumentId === document.id ? <CircularProgress size={16} aria-label="Validation en cours" /> : "Valider"}
                    </Button>
                  )}
                </Box>
              );
            }) : <Typography className={classes.emptyDocuments}>Aucun document transmis pour le moment.</Typography>}
          </Box>
        </Box>
      </Box>

      <Box className={classes.detailGrid}>
        <Box className={`${classes.card} ${classes.eligibilityCard}`}>
          <Typography className={classes.cardTitle}>Vérification d’éligibilité</Typography>
          {eligibilityRows.map((item) => (
            <Box className={classes.eligibilityRow} key={item.label}>
              <Box className={classes.eligibilityMetric}>
                <Typography className={classes.eligibilityLabel}>{item.label}</Typography>
                {item.detail && <Typography className={classes.eligibilityValue}>{item.detail}</Typography>}
              </Box>
              <Typography className={item.off ? classes.offText : item.good ? classes.conformText : classes.reviewText}>{item.text}</Typography>
              {item.off ? <span /> : item.good ? <CheckCircleRoundedIcon className={classes.eligibilityGood} /> : <WarningAmberRoundedIcon className={classes.documentPending} />}
            </Box>
          ))}
          {!eligibility && <Typography className={classes.eligibilityFootnote}>Estimation locale : l'API d'éligibilité n'a pas répondu.</Typography>}
          {needsReview && (
            <Box className={classes.warningBox}>
              <ErrorOutlineRoundedIcon />
              <Typography>Une dérogation est requise pour continuer.</Typography>
            </Box>
          )}
          <Divider className={classes.cardDivider} />
          <Typography className={classes.reasonTitle}>Motif de dérogation {notEligible && <span className={classes.required}>*</span>}</Typography>
          <TextField
            className={classes.reasonField}
            fullWidth
            multiline
            minRows={3}
            size="small"
            placeholder={notEligible ? "Obligatoire : pourquoi accorder le dossier malgré les contrôles ?" : "Facultatif"}
            value={overrideReason}
            disabled={Boolean(financing)}
            onChange={(event) => setOverrideReason(event.target.value)}
          />
        </Box>

        <Box className={classes.middleColumn}>
          <Box className={classes.card}>
            <Typography className={classes.cardTitle}>Informations du garant</Typography>
            <Box className={classes.guarantorRow}>
              <Avatar className={classes.guarantorAvatar}><PersonOutlineRoundedIcon /></Avatar>
              <Box className={classes.guarantorInfo}>
                <Typography className={classes.driverName}>{application.guarantorName}</Typography>
                <Typography className={classes.mutedLine}>{application.guarantorRelationship}</Typography>
                {application.guarantorPhone !== "--" && <Typography className={classes.contactLine}><LocalPhoneOutlinedIcon />{application.guarantorPhone}</Typography>}
                {application.guarantorEmail !== "--" && <Typography className={classes.contactLine}><MailOutlineRoundedIcon />{application.guarantorEmail}</Typography>}
                {application.guarantorAddress !== "--" && <Typography className={classes.contactLine}><LocationOnOutlinedIcon />{application.guarantorAddress}</Typography>}
                {application.guarantorIdNumber !== "--" && <Typography className={classes.mutedLine}>Pièce d'identité : {application.guarantorIdNumber}</Typography>}
              </Box>
            </Box>
          </Box>
          <Box className={classes.card}>
            <Typography className={classes.cardTitle}>Financement proposé</Typography>
            <Box className={classes.financeRow}><span>Véhicule</span><b>{vehicleText}</b></Box>
            {Number(application.quantity) > 1 && <Box className={classes.financeRow}><span>Quantité</span><b>{application.quantity}</b></Box>}
            {application.monthlyIncome != null && <Box className={classes.financeRow}><span>Revenu mensuel déclaré</span><b>{formatAmount(application.monthlyIncome)}</b></Box>}
            <Box className={classes.financeRow}><span>Charges mensuelles déclarées</span><b>{formatAmount(application.declaredCharges)}</b></Box>
            <Box className={classes.financeRow}><span>Prix comptant</span><b>{formatAmount(application.vehiclePrice)}</b></Box>
            <Box className={classes.financeRow}><span>Durée</span><b>{application.financingDuration ? `${application.financingDuration} mois` : "--"}</b></Box>
            <Box className={classes.financeRow}><span>Taux annuel</span><b>{application.annualRate == null ? "--" : `${application.annualRate} %`}</b></Box>
          </Box>
          {(financings.length > 0 || application.financingRefs.length > 0) && (
            <Box className={classes.card}>
              <Typography className={classes.cardTitle}>Dossiers ouverts</Typography>
              {(financings.length ? financings : application.financingRefs).map((item, index) => (
                <Box className={classes.financeRow} key={item.id || index}>
                  <span>{item.reference || `Véhicule ${index + 1}`}</span>
                  <Box className={classes.dossierLinkCell}>
                    <span className={`${classes.docBadge} ${classes[`docBadge-${FINANCING_STATUS[item.status]?.tone || "pending"}`]}`}>{FINANCING_STATUS[item.status]?.label || item.status || "--"}</span>
                    {item.id && <Button className={classes.linkButton} onClick={() => openDossier(item.id)}>Ouvrir</Button>}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box className={`${classes.card} ${classes.vehicleCard}`}>
          <Typography className={classes.cardTitle}>Réservation du véhicule</Typography>
          <Typography className={classes.optionalLabel}>(optionnelle)</Typography>
          {vehicleImageUrl && <Box className={classes.vehicleImage} style={vehicleImageStyle} role="img" aria-label={vehicleText} />}
          <Typography className={classes.vehicleName}>{vehicleText}</Typography>
          {vehicleMeta && <Typography className={`${classes.mutedLine} ${classes.vehicleMeta}`}>{vehicleMeta}</Typography>}
          {reservedAsset ? (
            <Typography className={classes.vehicleHint}>
              Véhicule réservé : {[reservedAsset.plate, reservedAsset.vin].filter(Boolean).join(" · ") || "--"}
              {reservedAsset.reserved_until ? ` jusqu'au ${new Date(reservedAsset.reserved_until).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : ""}
            </Typography>
          ) : financing ? (
            <Typography className={classes.vehicleHint}>Aucun véhicule réservé : la réservation se fait à l'étape Livraison.</Typography>
          ) : availableAssets.length ? (
            <>
              <TextField select size="small" fullWidth className={classes.assetSelect} value={selectedAsset} SelectProps={{ displayEmpty: true }} onChange={(event) => setSelectedAsset(event.target.value)}>
                <MenuItem value="">Ne pas réserver maintenant</MenuItem>
                {availableAssets.map((asset) => (
                  <MenuItem key={asset.id} value={asset.id}>
                    {[asset.plate, asset.vin, asset.location?.name || asset.location?.code].filter(Boolean).join(" · ")}
                  </MenuItem>
                ))}
              </TextField>
              <Typography className={classes.vehicleHint}>
                {availableAssets.length} véhicule(s) disponible(s). {selectedAsset ? "Il sera réservé 48 h à l'ouverture du dossier." : "Choisissez-en un pour le réserver 48 h à l'ouverture du dossier."}
              </Typography>
            </>
          ) : (
            <Typography className={classes.vehicleHint}>Aucun véhicule de ce modèle disponible en stock pour le moment.</Typography>
          )}
        </Box>
      </Box>

      <Box className={classes.footerActions}>
        {application.status === "PENDING" && (
          <Button className={classes.deleteButton} disabled={saving} onClick={() => setConfirmAction("delete")}>Supprimer la demande</Button>
        )}
        <span className={classes.footerSpacer} />
        {application.status === "PENDING" && (
          <Button className={classes.rejectButton} variant="outlined" disabled={saving} onClick={() => setConfirmAction("reject")}>Rejeter la demande</Button>
        )}
        {canOpenAnother && (
          <Button className={classes.rejectButton} variant="outlined" disabled={saving} onClick={startCreation}>
            Ouvrir le dossier du véhicule {openFinancings.length + 1}/{application.quantity}
          </Button>
        )}
        <Button className={classes.approveButton} variant="contained" disabled={saving} onClick={handleApprove}>
          {saving && !confirmAction ? "..." : financing ? "Continuer le dossier" : application.status === "APPROVED" ? "Ouvrir le dossier" : "Approuver et ouvrir le dossier"}
        </Button>
      </Box>

      <Dialog open={Boolean(previewDocument)} onClose={() => setPreviewDocument(null)} maxWidth="md" fullWidth aria-labelledby="identity-document-title">
        <DialogTitle id="identity-document-title">{previewDocument?.label}</DialogTitle>
        <DialogContent dividers>
          {previewDocument && (/^image\//i.test(previewDocument.mimeType) || /\.(png|jpe?g|gif|webp|avif|bmp)(?:[?#]|$)/i.test(previewDocument.url) ? (
            <Box component="img" src={previewDocument.url} alt={previewDocument.label} sx={{ display: "block", maxWidth: "100%", maxHeight: "65vh", margin: "auto", objectFit: "contain" }} />
          ) : (
            <Box component="iframe" src={previewDocument.url} title={previewDocument.label} sx={{ width: "100%", height: "65vh", border: 0 }} />
          ))}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap" }}>
          <Button href={previewDocument?.url} target="_blank" rel="noopener noreferrer">Ouvrir le fichier</Button>
          {previewDocument?.id && driverId && previewDocument.status !== "VALIDATED" && (
            <Button disabled={Boolean(validatingDocumentId)} onClick={() => validateDocument(previewDocument)}>
              {validatingDocumentId === previewDocument.id ? "Validation…" : "Valider le document"}
            </Button>
          )}
          {previewDocument?.status === "VALIDATED" && <Chip label="Validé" color="success" size="small" />}
          <Button onClick={() => setPreviewDocument(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmAction)} onClose={() => !saving && setConfirmAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle className={classes.confirmTitle}>
          {confirmAction === "delete" ? "Supprimer la demande ?" : "Rejeter la demande ?"}
        </DialogTitle>
        <DialogContent>
          <Typography className={classes.confirmText}>
            {confirmAction === "delete"
              ? "La demande sera masquée de la liste. Cette action est réservée aux demandes non traitées."
              : `La demande de ${application.driverName} passera au statut « Refusée ».`}
          </Typography>
        </DialogContent>
        <DialogActions className={classes.confirmActions}>
          <Button className={classes.rejectButton} variant="outlined" disabled={saving} onClick={() => setConfirmAction(null)}>Annuler</Button>
          <Button className={classes.dangerButton} variant="contained" disabled={saving} onClick={handleConfirm}>
            {saving ? "..." : confirmAction === "delete" ? "Supprimer" : "Rejeter"}
          </Button>
        </DialogActions>
      </Dialog>

      {driverId && (
        <DriverUpdateModal
          key={driverId}
          openDialog={driverModalOpen}
          handleClose={() => setDriverModalOpen(false)}
          driverId={driverId}
          entityLabel="Driver"
          showBankInformation={false}
          showMPlusInformation
          documentsTabLabel="Documents"
          documentsUserType="PROGRAM"
          statusVariant="PROGRAM"
          initialTab="documents"
        />
      )}
    </Box>
  );
};

export default ProgrammeMPlusReview;
